import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import type { Cma_Report_Source_Enum_Enum } from '@graphql/__generated__/graphql';
import { GET_CMA_REPORTS_LIST } from '@modules/reports/cma-reports/CMAReportsList';
import {
  GENERATE_CMA_REPORT,
  GET_CMA_REPORTS_COUNT,
} from '@modules/reports/cma-reports/cmaReportsQueries';

type GenerateCMAReportInput = {
  leadId: string;
  source: Cma_Report_Source_Enum_Enum;
};

type GenerateCMAReportHookResult = [
  ErrorLike | null,
  boolean,
  (input: GenerateCMAReportInput) => Promise<string | null>,
  () => void,
];

export const useGenerateCMAReport = (): GenerateCMAReportHookResult => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [generateCmaReportMutation, { loading }] = useMutation(
    GENERATE_CMA_REPORT,
    {
      refetchQueries: [GET_CMA_REPORTS_LIST, GET_CMA_REPORTS_COUNT],
      onError: setErrorLike,
    },
  );

  const generateCMAReport = useCallback(
    async ({ leadId, source }: GenerateCMAReportInput) => {
      const { data, error } = await generateCmaReportMutation({
        variables: {
          leadId,
          source,
        },
      });

      if (error || !data?.generate_cma_report?.cma_report_id) {
        return null;
      }

      return data.generate_cma_report.cma_report_id;
    },
    [generateCmaReportMutation],
  );

  return [apolloError, loading, generateCMAReport, () => setErrorLike(null)];
};
